import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { setTokenGetter } from './lib/api';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { ToastContainer } from './components/common/Toast';
import { LocalDevBanner } from './components/common/LocalDevBanner';
import { HomePage, DashboardPage, FrameworkPage, SubmitPage, ProjectDetailPage, ProfilePage, GapsPage } from './pages';
import { FrameworkConfigPage } from './pages/admin/FrameworkConfig';
import { UserManagementPage } from './pages/admin/UserManagement';
import { CapabilitiesPage } from './pages/admin/Capabilities';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

function AppRoutes() {
  const { getAccessToken, user } = useAuth();

  useEffect(() => {
    setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  return (
    <>
      <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/framework" element={<FrameworkPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/submit"
        element={
          <ProtectedRoute>
            <SubmitPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:id"
        element={
          <ProtectedRoute>
            <ProjectDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/framework"
        element={
          <ProtectedRoute>
            <FrameworkConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <UserManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/capabilities"
        element={
          <ProtectedRoute>
            <CapabilitiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gaps"
        element={
          <ProtectedRoute>
            <GapsPage />
          </ProtectedRoute>
        }
      />
      </Routes>
      {user && <LocalDevBanner role={user.role} />}
    </>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <ToastContainer />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
